import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Button, Card, EmptyState, ErrorText, Input, Spinner } from '@/components/ui'

import { AdminGuard } from './AdminGuard'
import { GlobalAiSettings, PersonalAiSettings } from './AiModelSettings'
import { apiUsers, usersKey } from './api'
import { UserUsage } from './UserUsage'
import { UsersList } from './UsersList'

function BudgetCard({ id }: { id: string }) {
  const client = useQueryClient()
  const user = useQuery({ queryKey: [...usersKey, 'detail', id], queryFn: () => apiUsers.get(id) })
  const [budget, setBudget] = useState<string | undefined>()
  const save = useMutation({
    mutationFn: () => apiUsers.patch(id, { ai_budget_rub: (budget ?? user.data?.ai_budget_rub ?? '') || null }),
    onSuccess: (value) => {
      client.setQueryData([...usersKey, 'detail', id], value)
      setBudget(undefined)
      void client.invalidateQueries({ queryKey: usersKey })
    },
  })
  if (user.isPending) return <Spinner />
  if (user.isError) return <ErrorText error={user.error} />
  return <Card title={`Бюджет: ${user.data.name || user.data.email}`} subtitle="Общий месячный лимит для LLM и Jev в рублях.">
    <form className="grid gap-3 sm:max-w-sm" onSubmit={(event: FormEvent) => { event.preventDefault(); if (!save.isPending) save.mutate() }}>
      <Input label="Месячный бюджет ИИ, ₽" type="number" min="0" step="any"
        value={budget ?? (Number(user.data.ai_budget_rub) === 0 && user.data.ai_budget_rub !== null ? '0' : user.data.ai_budget_rub ?? '')}
        onChange={(event) => setBudget(event.target.value)} hint="0 — ИИ выключен; пусто — без лимита." />
      <ErrorText error={save.error} />
      {save.isSuccess ? <p role="status" className="text-sm text-accent">Бюджет сохранён.</p> : null}
      <div><Button type="submit" variant="primary" loading={save.isPending}>Сохранить бюджет</Button></div>
    </form>
  </Card>
}

export function AdminAiPage() {
  return <AdminGuard><AiContent /></AdminGuard>
}

function AiContent() {
  const [params, setParams] = useSearchParams()
  const selected = params.get('user')
  return <div className="grid gap-5">
    <GlobalAiSettings />
    <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(16rem,20rem)_minmax(0,1fr)] xl:items-start">
      <UsersList allowInvite={false} selected={selected} onSelect={(id) => setParams({ user: id })} />
      {selected ? <div key={selected} className="grid min-w-0 gap-4">
        <BudgetCard id={selected} />
        <PersonalAiSettings userId={selected} />
        <UserUsage id={selected} />
      </div> : <EmptyState title="Выбери пользователя" description="Здесь появятся его модели, бюджет и статистика ИИ." />}
    </div>
  </div>
}
